/* ============================================================
   POUFUDIC Trust Badge 2026 - Counter Animation
   - IntersectionObserver: animate count up when badge scrolls into view
   - Respects prefers-reduced-motion
   - The real count is ALWAYS guaranteed: the counter can never be left
     showing a number lower than data-pf-trust-target.
   ============================================================ */
(function () {
  "use strict";

  if (window.__pfTrustBadgeLoaded__) return;
  window.__pfTrustBadgeLoaded__ = true;

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const hasRAF = typeof window.requestAnimationFrame === "function";

  function nowMs() {
    return window.performance && typeof performance.now === "function" ? performance.now() : Date.now();
  }

  // Thousands separators are a nicety, not a requirement - a broken Intl
  // polyfill must never cost us the number itself.
  function formatCount(n) {
    try {
      return n.toLocaleString();
    } catch (e) {
      return String(n);
    }
  }

  // Single source of truth for the finished state: writes the real target and
  // clears the animation class. Idempotent - safe to call any number of times.
  function settleCount(el, target) {
    if (!el || !target || isNaN(target)) return;
    el.__pfTrustSettled = true;
    el.__pfTrustAnimating = false;
    try {
      el.textContent = formatCount(target);
      el.classList.remove("is-animating");
    } catch (e) {
      /* nothing further we can do - the markup value stays on screen */
    }
  }

  // Digits currently on screen, locale separators stripped. Returns NaN when
  // the element shows nothing usable.
  function shownValue(el) {
    const digits = (el.textContent || "").replace(/[^0-9]/g, "");
    return digits ? parseInt(digits, 10) : NaN;
  }

  function animateCount(el, target, duration) {
    // No usable target: leave whatever the markup already rendered alone.
    if (!el || !target || isNaN(target)) return;

    // Anything that makes a count-up impossible or unwanted lands on the final
    // value instantly - never on 0.
    if (prefersReduced || !hasRAF || !duration || isNaN(duration)) {
      settleCount(el, target);
      return;
    }

    // Already done, or already counting - don't restart and don't re-zero.
    if (el.__pfTrustSettled || el.__pfTrustAnimating) return;
    el.__pfTrustAnimating = true;

    const start = 0;
    const startTime = nowMs();

    // Hard deadline. Whatever happens to the frame loop - frames never
    // delivered, tab throttled, a third-party rAF polyfill that swallows the
    // callback, a throw inside tick - the counter is forced onto the real
    // number once the animation window has elapsed.
    const deadline = setTimeout(function () {
      settleCount(el, target);
    }, duration + 400);

    function finish() {
      clearTimeout(deadline);
      settleCount(el, target);
    }

    // Only zero the counter now that the count-up is genuinely under way.
    try {
      el.textContent = formatCount(start);
      el.classList.add("is-animating");
    } catch (e) {
      finish();
      return;
    }

    // The frame timestamp argument is deliberately ignored: reading our own
    // clock keeps startTime and elapsed on the same time origin, so a polyfill
    // that passes Date.now() (or nothing at all) cannot poison progress.
    function tick() {
      try {
        const elapsed = nowMs() - startTime;
        const progress = Math.min(1, Math.max(0, elapsed / duration));
        // ease-out-cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = Math.round(start + (target - start) * eased);
        // A non-finite progress falls through to finish() rather than painting
        // "NaN" or freezing on 0.
        if (progress < 1) {
          el.textContent = formatCount(value);
          requestAnimationFrame(tick);
        } else {
          finish();
        }
      } catch (e) {
        finish();
      }
    }

    requestAnimationFrame(tick);
  }

  function initBadge(badge) {
    if (badge.__pfTrustInit) return;
    badge.__pfTrustInit = true;
    const counter = badge.querySelector("[data-pf-trust-target]");
    if (!counter) return;
    const target = parseInt(counter.getAttribute("data-pf-trust-target"), 10);
    if (!target || isNaN(target)) return;

    // The counter is deliberately NOT reset to "0" here. The markup already
    // ships the real number, and blanking it up front means any later failure
    // (observer never fires, no frames delivered, script error) leaves
    // "0 + Happy Customers" on screen forever. animateCount does the reset
    // itself, at the moment it actually starts counting.

    if (prefersReduced || !hasRAF || !("IntersectionObserver" in window)) {
      animateCount(counter, target, 1200);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCount(counter, target, 1200);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4, rootMargin: "0px 0px -10% 0px" }
    );
    observer.observe(badge);

    // Safety net: if the counter is showing less than the real number and
    // nothing is animating it, force the real number. A badge that is simply
    // still below the fold reads correctly already, so it keeps its animation.
    setTimeout(function () {
      if (counter.__pfTrustSettled || counter.__pfTrustAnimating) return;
      const shown = shownValue(counter);
      if (isNaN(shown) || shown < target) settleCount(counter, target);
    }, 2500);
  }

  function initAll() {
    document.querySelectorAll(".pf-trust").forEach(initBadge);
  }

  /* ============================================================
     Auto-injection: on product pages, replace the hardcoded
     `.product__rating-summary` block (or `.jdgm-prev-badge`)
     with our modern trust badge. Idempotent.
     ============================================================ */

  // The badge was built with a hardcoded rating of 5 while the store's real
  // rating is 4.9, so the buy box opened with five solid stars and screen
  // readers were told "Rated 5 out of 5". Partial-fill stars would not have
  // corrected that on their own: 4.9 is 98% of the way across a 60px strip,
  // which no eye can tell from a full star. The number itself is now printed
  // next to the stars, and it is what the aria-label announces. It prints bare
  // ("4.9", no "/5"): the pill is nowrap and at a 360px viewport it had only
  // 39.8px of slack inside its 340px column, so the suffix pushed it 3.6px past
  // the edge. The stars carry the scale on screen, the aria-label spells it out.
  function formatRating(n) {
    const v = Number(n);
    if (!v || isNaN(v)) return "4.9";
    return (Math.round(v * 10) / 10).toFixed(1);
  }

  function buildBadgeHTML(count, rating, href) {
    const c = count || 822;
    const r = formatRating(rating);
    // Customer avatars: real customer photos from window.PF_TRUST_AVATARS (set in theme.liquid via asset_url);
    // initial-gradient circles as fallback.
    const faces = Array.isArray(window.PF_TRUST_AVATARS) ? window.PF_TRUST_AVATARS.filter(Boolean) : [];
    var avatars, extra;
    if (faces.length) {
      avatars = faces.slice(0, 5).map(function (src, i) {
        return `<span class="pf-trust__avatar"><img src="${src}" alt="" width="30" height="30" loading="eager" decoding="async"${i === 0 ? ' fetchpriority="high"' : ""}></span>`;
      }).join("");
      extra = "";
    } else {
      var initials = [["S", "#E8B488", "#C97D5A"], ["E", "#7FB3A8", "#3F7569"], ["A", "#D9A86B", "#B0743A"], ["J", "#A28BBF", "#6B5A8A"]];
      avatars = initials.map(function (a) { return `<span class="pf-trust__avatar" style="--pf-g:linear-gradient(135deg, ${a[1]}, ${a[2]});">${a[0]}</span>`; }).join("");
      extra = `<span class="pf-trust__avatar pf-trust__avatar--extra">800+</span>`;
    }
    return `<a class="pf-trust" href="${href || "/pages/reviews"}" aria-label="Rated ${r} out of 5 from ${c} happy customers &mdash; see all reviews">
      <span class="pf-trust__avatars" aria-hidden="true">
        ${avatars}${extra}
      </span>
      <span class="pf-trust__text">
        <strong class="pf-trust__count" data-pf-trust-target="${c}">${c}</strong><span class="pf-trust__plus">+</span>
        <span class="pf-trust__label">Happy Customers</span>
      </span>
      <span class="pf-trust__stars" aria-hidden="true">
        <svg viewBox="0 0 60 12" width="60" height="12" focusable="false">
          <path transform="translate(0,0)" d="M6 0.5l1.65 3.6 3.95.4-2.95 2.7.85 3.85L6 9.1 2.5 11.05l.85-3.85L.4 4.5l3.95-.4z"/>
          <path transform="translate(12,0)" d="M6 0.5l1.65 3.6 3.95.4-2.95 2.7.85 3.85L6 9.1 2.5 11.05l.85-3.85L.4 4.5l3.95-.4z"/>
          <path transform="translate(24,0)" d="M6 0.5l1.65 3.6 3.95.4-2.95 2.7.85 3.85L6 9.1 2.5 11.05l.85-3.85L.4 4.5l3.95-.4z"/>
          <path transform="translate(36,0)" d="M6 0.5l1.65 3.6 3.95.4-2.95 2.7.85 3.85L6 9.1 2.5 11.05l.85-3.85L.4 4.5l3.95-.4z"/>
          <path transform="translate(48,0)" d="M6 0.5l1.65 3.6 3.95.4-2.95 2.7.85 3.85L6 9.1 2.5 11.05l.85-3.85L.4 4.5l3.95-.4z"/>
        </svg>
      </span>
      <span class="pf-trust__rating" style="display:inline-flex;align-items:baseline;flex-shrink:0;white-space:nowrap;line-height:1;font-weight:800;font-size:14px;font-variant-numeric:tabular-nums;">${r}</span>
      <span class="pf-trust__verified" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="16" height="16" focusable="false"><path d="M4 8.5l2.5 2.5L12 5.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </span>
    </a>`;
  }

  function injectOnProductPage() {
    // Only on product pages
    if (!document.body.classList.contains("template-product")) return;
    // Avoid double-injection
    if (document.querySelector(".pf-trust[data-pf-injected]")) return;

    // Target selectors in priority order (existing rating widgets)
    const selectors = [
      ".product__rating-summary",
      ".jdgm-prev-badge",
      ".productView-info .productView-rating",
      "[data-pf-trust-slot]"
    ];

    let target = null;
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) { target = el; break; }
    }
    if (!target) return;

    // Build wrapper
    const wrap = document.createElement("div");
    wrap.className = "pf-trust-wrap";
    wrap.style.margin = "2px 0 4px";
    // 4.9, not 5 - this call passed 5 and was the only place the rating was set.
    wrap.innerHTML = buildBadgeHTML(822, 4.9, "/pages/reviews");

    // Replace the target's contents
    target.innerHTML = "";
    target.appendChild(wrap);
    target.style.padding = "0";
    target.style.border = "0";
    target.style.marginTop = "2px";
    target.style.marginBottom = "0px";

    // Mark
    const newBadge = wrap.querySelector(".pf-trust");
    if (newBadge) {
      newBadge.setAttribute("data-pf-injected", "true");
      initBadge(newBadge);
    }
  }

  function buildSimilarReviewsBtn(handle) {
    return `<a class="pf-srb" href="/pages/reviews${handle ? "?product=" + encodeURIComponent(handle) : ""}" data-pf-srb-injected>
      <span class="pf-srb__icon" aria-hidden="true">
        <svg viewBox="0 0 18 18" width="16" height="16" focusable="false"><path d="M9 1.5l2.35 4.75 5.25.5-3.95 3.55 1.15 5.15L9 12.7l-4.8 2.75 1.15-5.15L1.4 6.75l5.25-.5z" fill="currentColor"/></svg>
      </span>
      <span class="pf-srb__text">
        <strong>See similar reviews</strong>
        <span class="pf-srb__sub">from customers who bought this</span>
      </span>
      <span class="pf-srb__arrow" aria-hidden="true">&rarr;</span>
    </a>`;
  }

  function injectSimilarReviewsButton() {
    if (!document.body.classList.contains("template-product")) return;
    if (document.querySelector("[data-pf-srb-injected]")) return;

    // Try common Halo/Ella PDP locations
    const anchors = [
      ".pf-usp",
      ".pfd-payments",
      ".product-form",
      ".productView-options",
      ".product__info",
      ".productView-info"
    ];
    let anchor = null;
    for (const sel of anchors) {
      const el = document.querySelector(sel);
      if (el) { anchor = el; break; }
    }
    if (!anchor) return;

    // Get product handle from current URL
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    const handle = m ? m[1] : "";

    const wrap = document.createElement("div");
    wrap.className = "pf-srb-wrap";
    wrap.style.margin = "12px 0";
    wrap.innerHTML = buildSimilarReviewsBtn(handle);

    // Insert after the anchor (so the button shows below the USPs / add-to-cart area)
    anchor.parentNode.insertBefore(wrap, anchor.nextSibling);
  }

  function bootstrap() {
    initAll();
    injectOnProductPage();
    // injectSimilarReviewsButton();  // removed per merchant request
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }

  // Re-init when Shopify section reloads or new badges are inserted
  document.addEventListener("shopify:section:load", bootstrap);
  document.addEventListener("shopify:section:select", bootstrap);

  // MutationObserver - catch dynamically inserted badges
  if ("MutationObserver" in window) {
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(".pf-trust")) initBadge(node);
          if (node.querySelectorAll) node.querySelectorAll(".pf-trust").forEach(initBadge);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
